import React, { useState, useEffect, ReactNode } from "react";
import SbuxTranslate from "../SbuxTranslate";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { styled } from "@mui/material/styles";
import { TabScrollButton } from "@mui/material";
import { useAppSelector } from "../../store/types";
import styles from "./styles";
import useCss from "../../hooks/useCss";
import {
  getActiveTabIdSelector,
  getTabsStatusSelector,
} from "../../selectors/tabsSelector";
import { getCcpInitializedSelector } from "../../selectors/ccpSelector";
import { getConnectedInstanceSelector } from "../../selectors/userInstanceSelector";

const ScrollButtonComponent = styled(TabScrollButton)(() => ({
  width: "20px",
  overflow: "hidden",
  transition: "width .1s",
  "&.Mui-disabled": {
    width: 0,
  },
}));

interface TabItem {
  tabId: string;
  label: string;
  visible?: boolean;
  [key: string]: unknown;
}

interface TabPanelItem {
  tabPanelId: string;
  visible?: boolean;
  component?: ReactNode;
  [key: string]: unknown;
}

interface Props {
  tabList: TabItem[];
  tabPanelList: TabPanelItem[];
  handleTabChange: (event: React.SyntheticEvent, newValue: string) => void;
}

const SbuxTabs = ({ tabList, tabPanelList, handleTabChange }: Props) => {
  const classes = useCss(styles);

  const activeTabId = useAppSelector(getActiveTabIdSelector);
  const tabsStatus = useAppSelector(getTabsStatusSelector);
  const isCcpInitialized = useAppSelector(getCcpInitializedSelector);
  const connectedInstance = useAppSelector(getConnectedInstanceSelector);

  const [tabs, setTabs] = useState<TabItem[]>([]);

  useEffect(() => {
    setTabs(tabList);
  }, [tabList]);

  const renderTabList = () => (
    <Box sx={{ width: "100%", borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}>
      <Tabs
        sx={{
          "&.MuiTabs-root": {
            minHeight: "37px !important",
          },
        }}
        value={activeTabId}
        onChange={handleTabChange}
        variant="fullWidth"
        slotProps={{
          indicator: {
            style: {
              display: "none",
            },
          },
        }}
      >
        {tabs.map(
          (tab, index) =>
            tab.visible && (
              <Tab
                sx={{ minHeight: "37px", height: "37px", fontSize: 16 }}
                label={<SbuxTranslate>{tab.label}</SbuxTranslate>}
                value={tab.tabId}
                key={index}
                className={classes.rootTab}
                disabled={tabsStatus === "enabled" ? false : true}
              />
            ),
        )}
      </Tabs>
    </Box>
  );

  const openURLsInNewTab = (urls: string[]) => {
    urls.forEach((url) => {
      window.open(url, "_blank");
    });
  };

  useEffect(() => {
    if (tabsStatus === "enabled" && isCcpInitialized) {
      const autoOpenURLs = connectedInstance?.autoOpenTargetURLs as
        | string[]
        | undefined;
      if (autoOpenURLs && autoOpenURLs.length > 0) {
        openURLsInNewTab(autoOpenURLs);
      }
    }
  }, [tabsStatus, isCcpInitialized, connectedInstance]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>{renderTabList()}</Box>
      {tabPanelList &&
        tabPanelList.map(
          (tabPanel, index) =>
            tabPanel.visible && (
              <Box
                key={index}
                hidden={activeTabId !== tabPanel.tabPanelId}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  display:
                    activeTabId === tabPanel.tabPanelId ? "flex" : "none",
                  flexDirection: "column",
                }}
              >
                {tabPanel.component}
              </Box>
            ),
        )}
    </Box>
  );
};

export default React.memo(SbuxTabs);
